import { isError } from '@/helpers/checkQuery'
import type { ResponseWrapper } from '@/api/types/base.types'
import type { AxiosResponse } from 'axios'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { crmIntegrationChatApi } from '../api/crm-integration-chat.api'
import type { CrmIntegrationConversation } from '../types/crm-integration-chat.types'
import { CRM_INTEGRATION_CHAT_MESSAGES_DEFAULT_LIMIT } from '../types/crm-integration-chat.types'
import type { CrmIntegrationMessageView } from '../types/crm-integration-message.types'
import { getCrmIntegrationApiErrorText } from '../lib/crmIntegrationApiErrors'
import {
  mergeCrmIntegrationMessagesPage,
  sortCrmIntegrationMessagesAsc,
} from '../lib/crmIntegrationMessagesMerge'
import { parseCrmIntegrationMessagesPayload } from '../lib/parseCrmIntegrationMessagesResponse'

export type CrmIntegrationMessagesLoadStatus = 'loading' | 'ready' | 'error'

export type CrmIntegrationServerMessagesUpdatedContext = {
  previousMessages: CrmIntegrationMessageView[]
  silent: boolean
  initialPageMessageCount: number
  conversation: CrmIntegrationConversation | null
}

export type UseCrmIntegrationMessagesLoadParams = {
  conversationId: number | null
  enabled?: boolean
  onServerMessagesUpdated: (
    nextServerMessages: CrmIntegrationMessageView[],
    context: CrmIntegrationServerMessagesUpdatedContext,
  ) => void
}

export type UseCrmIntegrationMessagesLoadResult = {
  status: CrmIntegrationMessagesLoadStatus
  conversation: CrmIntegrationConversation | null
  setConversation: Dispatch<SetStateAction<CrmIntegrationConversation | null>>
  serverMessages: CrmIntegrationMessageView[]
  setServerMessages: Dispatch<SetStateAction<CrmIntegrationMessageView[]>>
  serverMessagesRef: { current: CrmIntegrationMessageView[] }
  errorMessage: string | null
  loadMessages: (loadOptions?: { silent?: boolean }) => Promise<void>
}

export const useCrmIntegrationMessagesLoad = ({
  conversationId,
  enabled = true,
  onServerMessagesUpdated,
}: UseCrmIntegrationMessagesLoadParams): UseCrmIntegrationMessagesLoadResult => {
  const [status, setStatus] = useState<CrmIntegrationMessagesLoadStatus>('loading')
  const [conversation, setConversation] =
    useState<CrmIntegrationConversation | null>(null)
  const [serverMessages, setServerMessages] = useState<CrmIntegrationMessageView[]>(
    [],
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const conversationIdRef = useRef(conversationId)
  conversationIdRef.current = conversationId

  const serverMessagesRef = useRef(serverMessages)
  serverMessagesRef.current = serverMessages

  const onServerMessagesUpdatedRef = useRef(onServerMessagesUpdated)
  onServerMessagesUpdatedRef.current = onServerMessagesUpdated

  const loadMessages = useCallback(async (loadOptions?: { silent?: boolean }) => {
    const currentConversationId = conversationIdRef.current
    const previousMessages = serverMessagesRef.current

    if (currentConversationId == null) {
      if (!loadOptions?.silent) {
        setErrorMessage(null)
        setServerMessages([])
        setConversation(null)
      }
      return
    }

    if (!loadOptions?.silent) {
      setStatus('loading')
      setErrorMessage(null)
    }

    try {
      const res = await crmIntegrationChatApi.getIntegrationMessages({
        conversation_id: currentConversationId,
        limit: CRM_INTEGRATION_CHAT_MESSAGES_DEFAULT_LIMIT,
        offset: 0,
      })

      if (isError(res)) {
        if (loadOptions?.silent) {
          return
        }
        const text = getCrmIntegrationApiErrorText(
          res as AxiosResponse<ResponseWrapper<unknown>>,
          'Не удалось загрузить сообщения',
        )
        setStatus('error')
        setErrorMessage(text)
        return
      }

      const payload = res.data as ResponseWrapper<unknown>
      const parsed = parseCrmIntegrationMessagesPayload(payload?.data)
      const latestPage = sortCrmIntegrationMessagesAsc(parsed.items)
      const nextServerMessages = loadOptions?.silent
        ? mergeCrmIntegrationMessagesPage(previousMessages, latestPage)
        : latestPage

      setConversation(parsed.conversation)
      setServerMessages(nextServerMessages)
      onServerMessagesUpdatedRef.current(nextServerMessages, {
        previousMessages,
        silent: Boolean(loadOptions?.silent),
        initialPageMessageCount: latestPage.length,
        conversation: parsed.conversation,
      })

      setStatus('ready')
      setErrorMessage(null)
    } catch {
      if (loadOptions?.silent) {
        return
      }
      setStatus('error')
      setErrorMessage('Не удалось загрузить сообщения')
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setStatus('loading')
      setServerMessages([])
      setConversation(null)
      setErrorMessage(null)
      return
    }

    void loadMessages()
  }, [conversationId, enabled, loadMessages])

  return {
    status,
    conversation,
    setConversation,
    serverMessages,
    setServerMessages,
    serverMessagesRef,
    errorMessage,
    loadMessages,
  }
}
